import React, { useState } from 'react';
import { Wallet, Gem, ArrowRight, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMarblesBalance, useMarblesHistory } from '../../hooks/useMarblesWallet';

/**
 * Popover da carteira de Marbles no TopBar do console.
 * Substitui o antigo IconButton `<Wallet />` que não tinha onClick.
 *
 * Privacidade: só consulta `/tokens/balance` e `/tokens/history`
 * do usuário autenticado — nunca expõe saldo de terceiros.
 */

const MARBLE_COLOR = '#f9ca36';

export const WalletDropdown: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  // Só busca quando o popover abre (e o cache fica 30s) — evita fetches no carregamento da página
  const { data: balanceData, isPending: balancePending, isError: balanceError, refetch: refetchBalance } =
    useMarblesBalance(isOpen);
  const { data: transactions, isPending: historyPending, isError: historyError, refetch: refetchHistory } =
    useMarblesHistory(5, 0, isOpen);

  const balance = balanceData?.balance ?? 0;
  // isPending (e não isLoading): cobre o primeiro frame em que a query ainda
  // não começou a buscar — evita um flash de "0" antes de "Carregando..."
  const balanceLoading = balancePending;
  const historyLoading = historyPending;

  const handleRetry = () => {
    if (balanceError) refetchBalance();
    if (historyError) refetchHistory();
  };

  /** Rótulo i18n do tipo de transação, com fallback nunca exibindo chave crua */
  const txLabel = (type: string): string => {
    const label = t(`marbles.tx.${type}`, '');
    return label || type.replace(/_/g, ' ');
  };

  const openFullStatement = () => {
    setIsOpen(false);
    navigate('/settings?tab=wallet');
  };

  return (
    <div className="relative hidden sm:block">
      <button
        onClick={() => setIsOpen(!isOpen)}
        title={t('marbles.wallet_button', 'Carteira Marbles')}
        aria-label={t('marbles.wallet_button', 'Carteira Marbles')}
        className={`p-2 rounded-lg transition-all active:scale-95 ${
          isOpen
            ? 'text-[#f9ca36] bg-[#f9ca36]/10'
            : 'text-console-text/70 hover:text-[#f9ca36] hover:bg-[#f9ca36]/10'
        }`}
      >
        <Wallet size={18} />
      </button>

      {isOpen && (
        <>
          {/* Overlay invisível para fechar ao clicar fora */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

          <div className="absolute right-0 mt-2 w-80 bg-console-surface border border-console-border rounded-xl shadow-2xl z-50 overflow-hidden">
            {/* Cabeçalho: saldo */}
            <div className="relative p-4 border-b border-console-border overflow-hidden">
              <div
                className="absolute inset-0 opacity-[0.08] pointer-events-none"
                style={{ background: `radial-gradient(circle at top right, ${MARBLE_COLOR}, transparent 70%)` }}
              />
              <div className="relative flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Gem size={12} style={{ color: MARBLE_COLOR }} />
                    <span className="font-label text-[9px] font-black uppercase tracking-[0.25em] text-console-muted">
                      {t('marbles.title', 'Saldo Marbles')}
                    </span>
                  </div>
                  {balanceLoading ? (
                    <div className="flex items-center gap-1.5 text-console-muted font-label text-[11px] uppercase tracking-widest">
                      <RefreshCw size={12} className="animate-spin" />
                      {t('marbles.loading', 'Carregando...')}
                    </div>
                  ) : balanceError ? (
                    <button
                      onClick={handleRetry}
                      className="text-[11px] font-label font-bold uppercase tracking-widest text-red-400 hover:text-red-300 transition-colors"
                    >
                      {t('marbles.retry', 'Tentar novamente')}
                    </button>
                  ) : (
                    <div className="flex items-baseline gap-1.5">
                      <span
                        className="text-2xl font-display font-black italic tracking-tight"
                        style={{ color: MARBLE_COLOR }}
                      >
                        {balance}
                      </span>
                      <span className="font-label text-[10px] font-bold uppercase tracking-widest text-console-muted">
                        Marble{balance === 1 ? '' : 's'}
                      </span>
                    </div>
                  )}
                </div>
                
              </div>
            </div>

            {/* Últimas movimentações */}
            <div className="max-h-[280px] overflow-y-auto">
              <div className="px-4 pt-3 pb-1">
                <span className="font-label text-[9px] font-black uppercase tracking-[0.25em] text-console-muted">
                  {t('marbles.history_title', 'Extrato')}
                </span>
              </div>

              {historyLoading ? (
                <div className="p-6 text-center text-console-muted font-label text-[11px] uppercase tracking-widest">
                  {t('marbles.loading', 'Carregando...')}
                </div>
              ) : historyError ? (
                <div className="p-6 text-center">
                  <button
                    onClick={handleRetry}
                    className="text-[11px] font-label font-bold uppercase tracking-widest text-red-400 hover:text-red-300 transition-colors"
                  >
                    {t('marbles.retry', 'Tentar novamente')}
                  </button>
                </div>
              ) : !transactions || transactions.length === 0 ? (
                <div className="p-6 text-center">
                  <Gem size={22} className="mx-auto mb-2 opacity-30" style={{ color: MARBLE_COLOR }} />
                  <p className="font-label text-[11px] text-console-muted uppercase tracking-widest">
                    {t('marbles.empty', 'Nenhuma movimentação ainda')}
                  </p>
                </div>
              ) : (
                transactions.map((tx) => {
                  const isCredit = Number(tx.amount) >= 0;
                  return (
                    <div
                      key={tx.id}
                      className="px-4 py-2.5 border-b border-console-border/50 last:border-0 hover:bg-white/5 transition-colors flex items-start justify-between gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] text-console-text font-medium truncate" title={tx.description}>
                          {tx.description || txLabel(tx.event_type)}
                        </p>
                        <p className="font-label text-[9px] text-console-muted uppercase tracking-wider">
                          {txLabel(tx.event_type)} · {new Date(tx.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 font-display font-black italic text-xs tabular-nums ${
                          isCredit ? 'text-green-400' : 'text-red-400'
                        }`}
                      >
                        {isCredit ? '+' : '−'}
                        {Math.abs(Number(tx.amount) || 0)}
                      </span>
                    </div>
                  );
                })
              )}
            </div>

            {/* Rodapé: ações */}
            <div className="p-3 border-t border-console-border bg-console-bg/50">
              <button
                onClick={openFullStatement}
                className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[#f9ca36]/10 border border-[#f9ca36]/30 text-[#f9ca36] font-label text-[10px] font-black uppercase tracking-widest hover:bg-[#f9ca36]/20 transition-colors"
              >
                {t('marbles.view_full', 'Ver extrato completo')}
                <ArrowRight size={12} />
              </button>
              <p className="mt-2 text-center font-label text-[9px] text-console-muted/70 uppercase tracking-widest">
                {t('marbles.private_note', 'Somente você pode ver este saldo')}
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default WalletDropdown;
